import { useState } from 'react';
import { contactInfo } from '@data/siteData';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/Card';
import { MapPin, Mail, Train, Car, AlertCircle, Send, CheckCircle } from 'lucide-react';
import Input from '@components/ui/Input';
import Textarea from '@components/ui/Textarea';
import Button from '@components/ui/Button';
import { useSubmit } from '@hooks/useLoading';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface SubmitResult {
  success: boolean;
  message: string;
}

// Simulated API call for contact form submission
async function submitContactForm(data: ContactFormData): Promise<SubmitResult> {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1500));
  
  // Simulate validation (always succeeds in this demo)
  if (!data.name || !data.email || !data.subject || !data.message) {
    throw new Error('Bitte füllen Sie alle Felder aus.');
  }
  
  // Log for development
  console.log('Contact form submitted:', data);
  
  return {
    success: true,
    message: 'Vielen Dank für Ihre Nachricht! Wir werden uns so schnell wie möglich bei Ihnen melden.',
  };
}

export default function ContactPage() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const {
    isSubmitting,
    submitError,
    submitResult,
    submit,
    reset: resetSubmit,
  } = useSubmit<ContactFormData, SubmitResult>(submitContactForm, {
    timeout: 30000,
    onSuccess: () => {
      // Form stays in submitted state showing success message
    },
    onError: (err) => {
      console.error('Form submission error:', err);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submit(formData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: '',
    });
    resetSubmit();
  };

  const isSubmitted = submitResult?.success === true;

  return (
    <div className="py-12 bg-slate-950 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">Kontakt</h1>
          <p className="text-lg text-slate-400">
            Haben Sie Fragen oder möchten Sie mehr erfahren? Kontaktieren Sie uns.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-600/10 rounded-lg flex items-center justify-center">
                    <MapPin className="text-blue-400" size={20} />
                  </div>
                  <CardTitle>Adresse</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-300">
                  {contactInfo.address.street}
                  <br />
                  {contactInfo.address.postalCode} {contactInfo.address.city}
                </p>
                <p className="text-slate-400 text-sm mt-2">
                  Neben der Jesuitenkirche, gegenüber dem Mannheimer Schloss (Westflügel)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-600/10 rounded-lg flex items-center justify-center">
                    <Mail className="text-green-400" size={20} />
                  </div>
                  <CardTitle>E-Mail</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm mb-2">Allgemeine Anfragen:</p>
                <a
                  href={`mailto:${contactInfo.email.general}`}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {contactInfo.email.general}
                </a>
                <p className="text-slate-400 text-sm mt-4 mb-2">Veranstaltungen:</p>
                <a
                  href={`mailto:${contactInfo.email.events}`}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {contactInfo.email.events}
                </a>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-600/10 rounded-lg flex items-center justify-center">
                    <Train className="text-purple-400" size={20} />
                  </div>
                  <CardTitle>Öffentliche Verkehrsmittel</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Straßenbahn:</p>
                    {contactInfo.publicTransport.tram.map((line: string, index: number) => (
                      <p key={index} className="text-slate-300 text-sm">
                        {line}
                      </p>
                    ))}
                  </div>
                  <div>
                    <p className="text-slate-400 text-sm mb-1">Bus:</p>
                    {contactInfo.publicTransport.bus.map((line: string, index: number) => (
                      <p key={index} className="text-slate-300 text-sm">
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-600/10 rounded-lg flex items-center justify-center">
                    <Car className="text-amber-400" size={20} />
                  </div>
                  <CardTitle>Parken</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                {contactInfo.parking.map((option: string, index: number) => (
                  <p key={index} className="text-slate-300 text-sm mb-2">
                    • {option}
                  </p>
                ))}
              </CardContent>
            </Card>

            {/* Accessibility Note */}
            <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="text-yellow-500 mt-0.5 shrink-0" size={20} />
              <p className="text-yellow-200/80 text-sm">{contactInfo.accessibilityNote}</p>
            </div>
          </div>

          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle>Nachricht senden</CardTitle>
            </CardHeader>
            <CardContent>
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-600/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-green-400" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Vielen Dank für Ihre Nachricht!
                  </h3>
                  <p className="text-slate-400 mb-6">
                    {submitResult.message}
                  </p>
                  <Button variant="outline" onClick={handleReset}>
                    Neue Nachricht senden
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    placeholder="Ihr Name"
                  />
                  <Input
                    label="E-Mail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    placeholder="ihre@email.de"
                  />
                  <Input
                    label="Betreff"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    placeholder="Worum geht es?"
                  />
                  <Textarea
                    label="Nachricht"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows={5}
                    placeholder="Ihre Nachricht..."
                  />
                  
                  {/* Error message */}
                  {submitError && (
                    <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="text-red-400 shrink-0 mt-0.5" size={16} />
                      <p className="text-red-300 text-sm">
                        {submitError.message || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'}
                      </p>
                    </div>
                  )}
                  
                  <Button 
                    type="submit" 
                    className="w-full"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {!isSubmitting && <Send className="mr-2" size={18} />}
                    Nachricht senden
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
